import React, { useState, useMemo, useEffect } from "react";
import ActionBar from "@/components/atomics/action-bar";
import SearchInput from "@/components/atomics/search-input";
import WhitelistTable from "@/components/atomics/whitelist-table";
import LoadMoreButton from "@/components/atomics/load-more-button";
import { WalletListItem } from "@/services/backend";
import CheckIcon from "@/components/atomics/check-icon";
import WalletLimitWarning from "@/components/atomics/wallet-limit-warning";
import { useSnackbar } from "notistack";
import MotionModal from "@/components/atomics/motion-modal";
import { useCollectionContract } from "@/hooks/useCollectionContract";
import { useParams } from "react-router-dom";
import { useTonClient } from "@/hooks/useTonCLient";
import { useTonConnect } from "@/hooks/useTonConnect";
import BalanceBar from "@/components/atomics/balance-bar";

interface WhitelistManagerProps {
  whiteListItems: WalletListItem[];
  activeTab: string;
  mode: "add" | "remove";
  onWhitelistAction: (wallets: string[], isAdd: boolean) => void;
  showTotalCount?: boolean;
  preSelectedWallets?: Set<string>;
  onSelectionChange?: (selectedWallets: Set<string>) => void;
  loadMoreData?: (offset: number) => Promise<number>;
  showLessData?: () => void;
  canLoadMore?: boolean;
  canShowLess?: boolean;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onSearchChange?: (searchTerm: string) => void;
}

const WhitelistManager = ({
  whiteListItems,
  activeTab,
  mode,
  onWhitelistAction,
  showTotalCount,
  preSelectedWallets,
  onSelectionChange,
  loadMoreData,
  showLessData,
  canLoadMore = false,
  canShowLess = false,
  currentPage = 1,
  onPageChange,
  onSearchChange,
}: WhitelistManagerProps) => {
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(
    preSelectedWallets || new Set()
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showWalletLimitWarning, setShowWalletLimitWarning] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const WALLET_LIMIT = 70;

  useEffect(() => {
    if (preSelectedWallets) {
      setSelectedWallets(preSelectedWallets);
    }
  }, [preSelectedWallets]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedWallets);
    }
  }, [selectedWallets, onSelectionChange]);

  const displayedItems = whiteListItems;

  useEffect(() => {
    if (displayedItems.length === 0) {
      setIsAllSelected(false);
    } else {
      const allDisplayedSelected = displayedItems.every(item =>
        selectedWallets.has(item.wallet)
      );
      // If we've hit the wallet limit, consider "select all" as selected
      const hasHitLimit = selectedWallets.size >= WALLET_LIMIT;
      setIsAllSelected(
        (allDisplayedSelected && displayedItems.length > 0) || hasHitLimit
      );
    }
  }, [displayedItems, selectedWallets]);

  const handleSelectAll = () => {
    if (!isAllSelected) {
      const newSelectedWallets = new Set(selectedWallets);

      const walletsToAdd = displayedItems
        .filter(item => !selectedWallets.has(item.wallet))
        .map(item => item.wallet);

      const combinedWallets = [...Array.from(selectedWallets), ...walletsToAdd];

      if (combinedWallets.length > WALLET_LIMIT) {
        const limitedWallets = combinedWallets.slice(0, WALLET_LIMIT);
        setSelectedWallets(new Set(limitedWallets));
        setShowWalletLimitWarning(true);
        // Set isAllSelected to true since we hit the limit
        setIsAllSelected(true);
      } else {
        displayedItems.forEach(item => newSelectedWallets.add(item.wallet));
        setSelectedWallets(newSelectedWallets);
      }
    } else {
      const newSelectedWallets = new Set(selectedWallets);
      displayedItems.forEach(item => newSelectedWallets.delete(item.wallet));
      setSelectedWallets(newSelectedWallets);
    }
  };

  const toggleSelect = (wallet: string) => {
    const newSelectedWallets = new Set(selectedWallets);
    if (newSelectedWallets.has(wallet)) {
      newSelectedWallets.delete(wallet);
    } else {
      if (newSelectedWallets.size >= WALLET_LIMIT) {
        setShowWalletLimitWarning(true);
        return;
      }
      newSelectedWallets.add(wallet);
    }
    setSelectedWallets(newSelectedWallets);
  };

  const handleWhitelistAction = () => {
    if (selectedWallets.size === 0) {
      enqueueSnackbar(`Please select at least one wallet to ${mode}`, {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 2000,
      });
      return;
    }

    const wallets = Array.from(selectedWallets);
    onWhitelistAction(wallets, mode === "add" ? true : false);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleLoadMoreOrShowLess = async () => {
    setIsLoadingMore(true);
    try {
      if (canLoadMore) {
        if (loadMoreData) {
          const offset = whiteListItems.length;
          await loadMoreData(offset);
        }
      } else {
        if (showLessData) {
          showLessData();
        }
      }
    } catch (error) {
      console.error("Error in load more/show less:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const tableData = displayedItems;

  const closeWalletLimitWarning = () => {
    setShowWalletLimitWarning(false);
  };

  const handleWithdraw = async () => {};

  return (
    <>
      <div className="w-full px-[30px] pt-[10px] text-center pb-[10px]">
        <ActionBar
          action={mode}
          count={selectedWallets.size}
          onClick={handleWhitelistAction}
        />

        <div className="flex items-center justify-between gap-4 mt-[18px] mb-[10px]">
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => {
              handleSelectAll();
            }}
          >
            <div
              className={`relative w-[24px] h-[24px] pointer-events-auto border-2 rounded-sm flex items-center justify-center transition-all duration-200`}
            >
              {isAllSelected && (
                <CheckIcon
                  className="absolute left-[0px] top-[-7px] w-[31px] h-[28px]"
                  color="#FF0000"
                />
              )}
            </div>
          </div>
          <SearchInput
            className="flex-1 bg-light-gray border-none"
            placeholder="Search by wallet address..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {tableData.length === 0 ? (
          <div className="text-center py-[40px] text-gray-400 text-uppercase">
            {activeTab === "listed" ? (
              <>
                <p>No listed wallets</p>
                <p className="text-sm mt-2">
                  First, go to Requests, choose wallets, then press ‘Add
                  Selected to Whitelist’
                </p>
              </>
            ) : (
              <>
                <p>you have No requests</p>
                <p className="text-sm mt-2">
                  Share the collection page link so users can submit their
                  requests
                </p>
              </>
            )}
          </div>
        ) : (
          <WhitelistTable
            whitelist={tableData}
            toggleSelect={toggleSelect}
            selectedWallets={selectedWallets}
            showCheckbox={activeTab !== "eligible"}
            showRemove={activeTab === "eligible"}
          />
        )}

        {(canLoadMore || canShowLess) && (
          <LoadMoreButton
            isLoading={isLoadingMore}
            hasMore={canLoadMore}
            onLoadMore={handleLoadMoreOrShowLess}
          />
        )}
      </div>
      <MotionModal isOpen={showWalletLimitWarning}>
        <WalletLimitWarning
          className="w-full"
          onClose={closeWalletLimitWarning}
          onClick={closeWalletLimitWarning}
        />
      </MotionModal>
    </>
  );
};

export default WhitelistManager;
